import React, { useState } from 'react';
import { KeyRound, Loader2, ShieldCheck, Mail, ArrowLeft } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useLeadAttendance } from '../../context/LeadAttendanceContext';
import LanguageSwitcher from './LanguageSwitcher';

interface LeadAuthScreenProps {
  isAuthenticating: boolean;
  onSubmit: (leadCode: string) => Promise<void>;
  error?: string | null;
}

export default function LeadAuthScreen({ isAuthenticating, onSubmit, error }: LeadAuthScreenProps) {
  const { t, i18n } = useTranslation();
  const { apiUrl } = useLeadAttendance();

  const [leadCode, setLeadCode] = useState('');
  const [isForgotMode, setIsForgotMode] = useState(false);
  const [email, setEmail] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [forgotMessage, setForgotMessage] = useState<{ type: 'error' | 'success'; text: string } | null>(null);

  const handleCodeChange = (value: string) => {
    const digitsOnly = value.replace(/\D/g, '').slice(0, 8);
    setLeadCode(digitsOnly);
  };

  const handleLoginSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (leadCode.length !== 8) return;
    await onSubmit(leadCode);
  };

  const handleForgotSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!email.includes('@')) {
      setForgotMessage({ type: 'error', text: t('invalidEmail') });
      return;
    }

    setIsSending(true);
    setForgotMessage(null);

    try {
      const response = await fetch(`${apiUrl}/chatbot/forgot-code`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, language: i18n.language }),
      });

      if (response.ok) {
        setForgotMessage({ type: 'success', text: t('codeSent') });
        setTimeout(() => setIsForgotMode(false), 3000);
      } else {
        setForgotMessage({ type: 'error', text: t('forgotCodeError') });
      }
    } catch (err) {
      setForgotMessage({ type: 'error', text: t('forgotCodeError') });
    } finally {
      setIsSending(false);
    }
  };

  return (
    <section className="min-h-screen bg-slate-50 px-4 py-8">
      {/* Language Switcher */}
      <div className="absolute right-4 top-4">
        <LanguageSwitcher variant="light" />
      </div>

      <div className="mx-auto flex min-h-[85vh] w-full max-w-md items-center justify-center">
        <div className="w-full rounded-3xl border border-slate-200 bg-white p-6 shadow-card sm:p-7 transition-all duration-300">
          
          {!isForgotMode ? (
            <>
              <div className="mb-5 flex items-center gap-3">
                <div className="rounded-xl bg-brand-100 p-2 text-brand-700">
                  <ShieldCheck className="h-6 w-6" />
                </div>
                <div>
                  <h1 className="text-xl font-semibold text-slate-900">{t('brandTitle') || 'Giveful Lead Attendance'}</h1>
                  <p className="text-sm text-slate-500">{t('signInSubtitle') || 'Use your 8-digit lead code to continue.'}</p>
                </div>
              </div>

              <form className="space-y-4" onSubmit={handleLoginSubmit}>
                <label className="block text-sm font-medium text-slate-700" htmlFor="leadCode">
                  {t('leadCodeLabel') || 'Lead Code'}
                </label>

                <div className="relative">
                  <KeyRound className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                  <input
                    id="leadCode"
                    type="text"
                    inputMode="numeric"
                    autoComplete="off"
                    autoFocus
                    value={leadCode}
                    maxLength={8}
                    onChange={(event) => handleCodeChange(event.target.value)}
                    placeholder={t('leadCodePlaceholder') || '12345678'}
                    className="h-12 w-full rounded-xl border border-slate-300 pl-10 pr-3 text-base text-slate-900 outline-none transition focus:border-brand-600 focus:ring-2 focus:ring-brand-100"
                  />
                </div>

                <div className="flex justify-end">
                  <button
                    type="button"
                    onClick={() => { setIsForgotMode(true); setForgotMessage(null); }}
                    className="text-sm font-medium text-brand-600 hover:text-brand-700"
                  >
                    {t('forgotCodeLink')}
                  </button>
                </div>

                <button
                  type="submit"
                  disabled={isAuthenticating || leadCode.length !== 8}
                  className="flex h-12 w-full items-center justify-center gap-2 rounded-xl bg-brand-700 text-base font-semibold text-white transition hover:bg-brand-800 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {isAuthenticating ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      {t('authLoading') || 'Signing in...'}
                    </>
                  ) : (
                    t('continue') || 'Continue'
                  )}
                </button>
              </form>

              {error && (
                <p className="mt-4 rounded-xl border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
                  {error}
                </p>
              )}
            </>
          ) : (
            <>
              <div className="mb-5 flex items-start gap-3 flex-col relative pt-8">
                <button 
                  onClick={() => setIsForgotMode(false)}
                  className="absolute left-0 top-0 flex items-center gap-1 text-sm text-slate-500 hover:text-slate-800 transition"
                  type="button"
                >
                  <ArrowLeft className="h-4 w-4" /> {t('cancel')}
                </button>
                <div>
                  <h1 className="text-xl font-semibold text-slate-900">{t('forgotCodeTitle')}</h1>
                  <p className="text-sm text-slate-500 mt-1">{t('forgotCodeSubtitle')}</p>
                </div>
              </div>

              <form className="space-y-4" onSubmit={handleForgotSubmit}>
                <label className="block text-sm font-medium text-slate-700" htmlFor="email">
                  {t('emailLabel')}
                </label>

                <div className="relative">
                  <Mail className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                  <input
                    id="email"
                    type="email"
                    autoComplete="email"
                    autoFocus
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder={t('emailPlaceholder')}
                    className="h-12 w-full rounded-xl border border-slate-300 pl-10 pr-3 text-base text-slate-900 outline-none transition focus:border-brand-600 focus:ring-2 focus:ring-brand-100"
                  />
                </div>

                <button
                  type="submit"
                  disabled={isSending || !email}
                  className="flex h-12 w-full items-center justify-center gap-2 rounded-xl bg-brand-700 text-base font-semibold text-white transition hover:bg-brand-800 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {isSending ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      {t('sendingCode')}
                    </>
                  ) : (
                    t('sendCode')
                  )}
                </button>
              </form>

              {forgotMessage && (
                <p className={`mt-4 rounded-xl border px-3 py-2 text-sm ${
                  forgotMessage.type === 'error' 
                    ? 'border-rose-200 bg-rose-50 text-rose-700' 
                    : 'border-emerald-200 bg-emerald-50 text-emerald-700'
                }`}>
                  {forgotMessage.text}
                </p>
              )}
            </>
          )}

        </div>
      </div>
    </section>
  );
}
